import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/store';

import FormModal from '../../Modals/FormModal';
import RecordTable from '../../Tables/record/RecordTable';
import PurchaseInfoCard from '../common/PurchaseInfoCard';
import PaymentConditionSection from '../common/PaymentConditionSection';
import SvgIcon from '../../SvgIcon/SvgIcon';

import AddReturnProductForm from './AddReturnProductForm';
import AddDiscountProductForm from './AddDiscountProductForm';

import { useSupplier } from '../../../hooks/useSupplier';
import { useProduct } from '../../../hooks/useProduct';
import { useCreditNote } from '../../../hooks/purchases/useCreditNotePurchase';

import { creditNoteProductColumns } from '../../../features/purchases/creditNote/creditNoteProductColumns';

interface Product {
  id: number; // ← ID del detalle (nuevo, necesario para eliminar)
  productName: string;
  productCode: string;
  unitName: string;
  categoryName: string;
  quantity: number;
  unitCost: number;
  totalAmount: number;
  ivaAmount: number;
  type: 'RETURN' | 'DISCOUNT';
  distributionPercent: number;
  purchaseReferenceCode: string;
}

const CreditNoteSection = () => {
  const [isAddDiscountOpen, setIsAddDiscountOpen] = useState(false);
  const [isAddReturnOpen, setIsAddReturnOpen] = useState(false);

  const { handlerAddProduct, handlerDeleteProduct } = useCreditNote();

  const { form } = useSelector((state: RootState) => state.basePurchase);

  const { suppliersDropdown, handlerGetSuppliersForDropdown } = useSupplier();
  const { handlerGetProductsForDropdownByItemType } = useProduct();

  useEffect(() => {
    handlerGetSuppliersForDropdown();
    handlerGetProductsForDropdownByItemType(1);
  }, []);

  const columns = useMemo(() => creditNoteProductColumns, []);

  const actions = [
    {
      key: 'delete',
      onClick: (row: Product) => {
        handlerDeleteProduct(row.id);
      },
    },
  ];

  return (
    <div>
      <PurchaseInfoCard
        suppliers={suppliersDropdown}
        title="Compra de Nota de Crédito"
        maxWidth="w-full"
      />

      <RecordTable
        maxWidth="w-full"
        columns={columns}
        data={form.products}
        actions={actions}
        noDataMessage="No hay productos agregados."
        tableHeaderContent={
          <h3 className="text-lg font-medium">
            Productos de la Nota de Crédito
          </h3>
        }
        tableHeaderActions={
          <>
            <button
              onClick={() => setIsAddReturnOpen(true)}
              className="flex items-center justify-center gap-2 rounded bg-primary px-4.5 py-2.5 font-medium text-white hover:bg-opacity-90"
            >
              <SvgIcon name="return" />
              Por Devolución
            </button>
            <button
              onClick={() => setIsAddDiscountOpen(true)}
              className="flex items-center justify-center gap-2 rounded bg-primary px-4.5 py-2.5 font-medium text-white hover:bg-opacity-90"
            >
              <SvgIcon name="discount" />
              Por Descuento
            </button>
          </>
        }
      />

      <FormModal
        isOpen={isAddReturnOpen}
        onClose={() => setIsAddReturnOpen(false)}
        title="Producto por Devolución"
        maxWidth="max-w-4xl"
      >
        <AddReturnProductForm
          onAdd={(product) => {
            handlerAddProduct(product);
            setIsAddReturnOpen(false);
          }}
        />
      </FormModal>

      <FormModal
        isOpen={isAddDiscountOpen}
        onClose={() => setIsAddDiscountOpen(false)}
        title="Producto por Descuento"
        maxWidth="max-w-4xl"
      >
        <AddDiscountProductForm
          onAdd={(product) => {
            handlerAddProduct(product);
            setIsAddDiscountOpen(false);
          }}
        />
      </FormModal>

      <PaymentConditionSection />
    </div>
  );
};

export default CreditNoteSection;
